<template>
  <div class="grid gap-3 mt-4" :class="gridClass">
    <template v-for="field in customFields" :key="field.id">
      <div class="theme-input hrm_input">
        <div class="custom-field">
          <label :for="field.id" class="hrm_input_label text-sm font-medium text-gray-800 dark:text-gray-400">{{
            field.attribute_label }}</label>
          <div class="relative">
            <template v-if="field.attribute_type === 'textarea'">
              <textarea :id="field.id" v-model="customFieldValues[field.id]"
                class="hrm_input_field mt-2 block text-sm px-2.5 pb-2 pt-2 w-full shadow-transparent text-gray-400 dark:text-gray-100 dark:bg-slate-700 bg-gray-300 rounded-sm border border-gray-400 focus:border-amber-500 outline-0 focus:outline-0"></textarea>
            </template>
            <template v-else-if="field.attribute_type === 'select'">
              <template v-if="field.options && field?.options?.type === 'select'">
                <VueMultiselect mode="single" class="hrm_input_field mt-2" v-model="customFieldValues[field.id]"
                  :options="field.options.options" :placeholder="trans('lang.labels.Select')" label="label"
                  valueProp="value" :searchable="true" :hideSelected="false">
                  <template v-slot:singlelabel="{ value }">
                    <div class="multiselect-single-label">
                      {{ value.label }}
                    </div>
                  </template>
                  <template v-slot:option="{ option }">
                    <span>
                      {{ option.label }}
                    </span>
                  </template>
                </VueMultiselect>
              </template>
            </template>
            <template v-else-if="field.attribute_type === 'google_map'">
              <google-map :id="field.id" v-model="customFieldValues[field.id]"
                class="hrm_input_field mt-2 block text-sm px-2.5 pb-2 pt-2 w-full shadow-transparent text-gray-400 dark:text-gray-100 dark:bg-slate-700 bg-gray-300 rounded-sm border border-gray-400 focus:border-amber-500 outline-0 focus:outline-0"></google-map>
            </template>
            <template v-else-if="field.attribute_type === 'date'">
              <DatePicker class="hrm_input_field mt-2" :vTimePicker="false" @submitDate="getDate" :vMonthPicker="false"
                :vEnableTime="false" :dateData="customFieldValues[field.id]" :field="field" placeholder="Select Date">
              </DatePicker>
            </template>
            <template v-else-if="field.attribute_type === 'time'">
              <DatePicker class="hrm_input_field mt-2" format_header="H:i" :vTimePicker="true" :vMonthPicker="false"
                :vEnableTime="true" :time="true" hide-input-icon @submitTime="getTime" :field="field"
                placeholder="Select Time" :dateData="customFieldValues[field.id]">
              </DatePicker>
            </template>
            <template v-else-if="field.attribute_type === 'color'">
              <div class="hrm_input_field flex items-center gap-2 mt-2">
                <div class="w-8 h-7 rounded-lg" :style="{ backgroundColor: customFieldValues[field.id] }"></div>
                <input type="color" :id="field.id" v-model="customFieldValues[field.id]"
                  class="hrm_input_field block text-sm px-2.5 pb-2 pt-2 w-full shadow-transparent text-gray-400 dark:text-gray-100 dark:bg-slate-700 bg-gray-300 rounded-sm border border-gray-400 focus:border-amber-500 outline-0 focus:outline-0" />
              </div>
            </template>
            <template v-else-if="field.attribute_type === 'password'">
              <div class="hrm_input_field flex items-center gap-2 mt-2">
                <input :type="passwordVisibility[field.id] ? 'text' : 'password'" :id="field.id"
                  v-model="customFieldValues[field.id]" autocomplete="password"
                  class="hrm_input_field block text-sm px-2.5 pb-2 pt-2 w-full shadow-transparent text-gray-400 dark:text-gray-100 dark:bg-slate-700 bg-gray-300 rounded-sm border border-gray-400 focus:border-amber-500 outline-0 focus:outline-0" />
                <div @click="togglePassword(field.id)"
                  class="cursor-pointer flex items-center justify-center gap-2 bg-hrmThemeBgColor text-hrmThemeTextColor px-2 py-1.5 rounded-lg">
                  <Icon :icon="passwordVisibility[field.id] ? 'heroicons:eye-slash-16-solid' : 'ri:eye-fill'" width="20"
                    height="20" />
                </div>
              </div>
            </template>
            <template v-else-if="field.attribute_type === 'file'">
              {{ localCustomFieldValues }}
              <input type="file" :id="field.id" @change="handleFileChange($event, field.id)"
                class="hrm_input_field mt-2 block text-sm px-2.5 w-full shadow-transparent text-gray-400 dark:text-gray-100 dark:bg-slate-700 bg-gray-300 rounded-sm border border-gray-400 focus:border-amber-500 outline-0 focus:outline-0" />
            </template>


            <input v-else :type="field.attribute_type" :id="field.id" v-model="customFieldValues[field.id]"
              class="hrm_input_field mt-2 block text-sm px-2.5 pb-2 pt-2 w-full shadow-transparent text-gray-400 dark:text-gray-100 dark:bg-slate-700 bg-gray-300 rounded-sm border border-gray-400 focus:border-amber-500 outline-0 focus:outline-0" />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted } from "vue";
import axios from "axios";
import DatePicker from "@/Common/DatePicker.vue";
import VueMultiselect from "@vueform/multiselect";
import { dateTimeFormatServer, trans } from "@/helpers";
import { Icon } from "@iconify/vue";

/* =========================
   Props
========================= */

interface Props {
  modelType: string;
  position: number | string;
  value?: Record<string, any>;
  customFieldValues?: Record<string, any>;
  gridClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  value: () => ({}),
  customFieldValues: () => ({}),
  gridClass: "lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1",
});

/* =========================
   Emits
========================= */

const emit = defineEmits<{
  // (e: "update:modelValue", value: Record<string, any>): void;
  (e: "input", value: Record<string, any>): void;
}>();

/* =========================
   State
========================= */

const customFields = ref<any[]>([]);
const passwordVisibility = reactive<Record<string | number, boolean>>({});

/*
|--------------------------------------------------------------------------
| Local Reactive Copy (VERY IMPORTANT)
|--------------------------------------------------------------------------
*/

const localCustomFieldValues = reactive<Record<string | number, any>>({});

/*
|--------------------------------------------------------------------------
| Sync Props → Local State
|--------------------------------------------------------------------------
*/

watch(
  () => props.customFieldValues,
  (newVal) => {
    Object.keys(localCustomFieldValues).forEach((key) => {
      delete localCustomFieldValues[key];
    });

    Object.assign(localCustomFieldValues, newVal);
  },
  { immediate: true, deep: true }
);

/*
|--------------------------------------------------------------------------
| Emit Changes To Parent
|--------------------------------------------------------------------------
*/

watch(
  localCustomFieldValues,
  (val) => {
    emit("update:modelValue", { ...val });
    emit("input", { ...val });
  },
  { deep: true }
);

/* =========================
   Methods
========================= */

const getDate = (date: any, field: any) => {
  if (!date) {
    localCustomFieldValues[field.id] = null;
  } else {
    localCustomFieldValues[field.id] = dateTimeFormatServer(date);
  }
};

const getTime = (time: any, field: any) => {
  if (!time) {
    localCustomFieldValues[field.id] = null;
  } else {
    localCustomFieldValues[field.id] = time;
  }
};

const togglePassword = (id: string | number) => {
  passwordVisibility[id] = !passwordVisibility[id];
};

/*
|--------------------------------------------------------------------------
| FILE HANDLER (FIXED)
|--------------------------------------------------------------------------
*/

const handleFileChange = (event: Event, fieldId: number | string) => {
  const target = event.target as HTMLInputElement;
  if (!target.files || target.files.length === 0) {
    localCustomFieldValues[fieldId] = null;
    return;
  }

  const file = target.files[0];
  
  // Store actual File object (NOT fake path)
  localCustomFieldValues[fieldId] = file;
};

/* =========================
   Lifecycle
========================= */

onMounted(async () => {
  try {
    const response = await axios.get(
      route("customfield.custom-fields.index"),
      {
        params: {
          customfieldable_type: props.modelType,
          status: 1,
        },
      }
    );

    customFields.value = response.data.data || [];
  } catch (error) {
    console.error("Error fetching custom fields:", error);
  }
});
</script>



<style scoped>
.custom-field {
  margin-bottom: 1rem;
}
</style>
